import React from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import PrintIcon from '@material-ui/icons/Print';
import DescriptionIcon from '@material-ui/icons/Description';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import SearchArea from './SearchArea';
import { getBuyerValidationErrors, sanitizeBuyerField } from './buyerValidation';
import GeneralService from '../apis/GeneralService';
import { Panel } from 'primereact/panel';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: "#2f7ea8",
        color: theme.palette.common.white,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        padding: '10px 12px',
    },
    body: {
        fontSize: 12,
        color: '#263238',
        lineHeight: 1.35,
        padding: '10px 12px',
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: '#f7fafc',
        },
        '&:hover': {
            backgroundColor: '#eef7fb',
        },
    },
}))(TableRow);

const useStyles = makeStyles({
    pageShell: {
        maxWidth: '1220px',
        margin: '0 auto',
        padding: '22px 20px 42px',
        color: '#263238',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        '& .p-panel': {
            border: '1px solid #d9e6ef',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 10px 28px rgba(38, 50, 56, 0.07)',
            backgroundColor: '#ffffff',
        },
        '& .p-panel .p-panel-header': {
            backgroundColor: '#f6fafc',
            borderBottom: '1px solid #d9e6ef',
            color: '#263238',
            fontSize: '15px',
            fontWeight: 700,
            justifyContent: 'center',
            padding: '12px 16px',
            letterSpacing: 0,
        },
        '& .p-panel .p-panel-content': {
            padding: '16px',
            border: 0,
        },
    },
    sectionSpacer: {
        marginTop: '18px',
    },
    table: {
        minWidth: 700,
        '& input[type="checkbox"]': {
            width: 16,
            height: 16,
            cursor: 'pointer',
        },
    },
    printButton: {
        marginBottom: '20px',
        backgroundColor: '#2f7ea8',
        color: 'white',
        borderRadius: '6px',
        padding: '9px 16px',
        fontWeight: 700,
        fontSize: '12px',
        boxShadow: '0 8px 18px rgba(47, 126, 168, 0.22)',
        '&:hover': {
            backgroundColor: '#286f95',
        },
    },
    selectedFormContainer: {
        marginBottom: '20px',
        padding: '18px 20px',
        backgroundColor: '#ffffff',
        border: '1px solid #d9e6ef',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        boxShadow: '0 8px 22px rgba(38, 50, 56, 0.06)',
    },
    formInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#263238',
    },
    paymentContainer: {
        marginTop: '24px',
        border: '1px solid #d7e5ee',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        boxShadow: '0 16px 36px rgba(38, 50, 56, 0.10)',
    },
    paymentHeader: {
        padding: '22px 26px',
        borderBottom: '1px solid #d7e5ee',
        background: 'linear-gradient(180deg, #f7fbfd 0%, #eef7fb 100%)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '18px',
        flexWrap: 'wrap',
    },
    paymentEyebrow: {
        margin: '0 0 5px 0',
        fontSize: '11px',
        color: '#607d8b',
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: 0,
    },
    paymentBrandTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: 700,
        color: '#263238',
        letterSpacing: 0,
    },
    paymentBrandSubtitle: {
        margin: '6px 0 0 0',
        color: '#607d8b',
        fontSize: '12px',
        lineHeight: 1.45,
        maxWidth: 520,
    },
    paymentLogos: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        flexWrap: 'wrap',
        padding: '10px 12px',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        border: '1px solid #dbe8f0',
    },
    placetopayLogo: {
        height: '30px',
        width: 'auto',
        display: 'block',
    },
    franchiseLogo: {
        height: '32px',
        width: 'auto',
        maxWidth: '100%',
        display: 'block',
    },
    paymentBody: {
        padding: '24px 26px 26px',
    },
    paymentLayout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 0.85fr) minmax(0, 1.6fr)',
        gap: '22px',
        alignItems: 'start',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
        },
    },
    paymentFormPanel: {
        border: '1px solid #dce8f0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        padding: '18px',
    },
    paymentFormTitle: {
        margin: '0 0 14px 0',
        color: '#263238',
        fontSize: '15px',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    paymentGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px',
        '& label': {
            color: '#37474f',
            fontSize: '11px',
            fontWeight: 700,
            lineHeight: 1.5,
        },
    },
    paymentInput: {
        width: '100%',
        boxSizing: 'border-box',
        marginTop: '5px',
        padding: '10px 11px',
        border: '1px solid #cbd8e1',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#263238',
        backgroundColor: '#ffffff',
        outline: 'none',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:focus': {
            borderColor: '#2f7ea8',
            boxShadow: '0 0 0 3px rgba(47, 126, 168, 0.12)',
        },
    },
    paymentInputInvalid: {
        borderColor: '#d32f2f',
        backgroundColor: '#fffafa',
        '&:focus': {
            borderColor: '#d32f2f',
            boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.10)',
        },
    },
    fieldErrorText: {
        display: 'block',
        marginTop: '5px',
        color: '#b71c1c',
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: 1.35,
    },
    paymentSummary: {
        padding: '0',
        borderRadius: '8px',
        backgroundColor: '#fff',
        border: '1px solid #dce8f0',
        overflow: 'hidden',
        position: 'sticky',
        top: '16px',
    },
    paymentSummaryHeader: {
        padding: '16px 18px',
        backgroundColor: '#f6fafc',
        borderBottom: '1px solid #e3edf4',
        '& h4': {
            margin: 0,
            color: '#263238',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: 1.35,
        },
        '& p': {
            margin: '5px 0 0 0',
            color: '#607d8b',
            fontSize: '11px',
            lineHeight: 1.4,
        },
    },
    paymentSummaryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '11px 16px',
        borderBottom: '1px solid #edf2f7',
        color: '#263238',
        '& span': {
            color: '#607d8b',
            fontWeight: 600,
            fontSize: '11px',
        },
        '& strong': {
            fontWeight: 700,
            fontSize: '12px',
        },
    },
    paymentSummaryTotal: {
        backgroundColor: '#eef7fb',
        fontSize: '13px',
        borderBottom: 0,
        '& span': {
            color: '#263238',
            fontWeight: 700,
        },
        '& strong': {
            color: '#263238',
            fontSize: '14px',
        },
    },
    paymentSummaryActions: {
        padding: '14px 16px',
        textAlign: 'stretch',
        '& button': {
            width: '100%',
            borderRadius: '6px',
            fontWeight: 700,
        },
    },
    termsBox: {
        marginTop: '12px',
        padding: '12px 14px',
        borderRadius: '6px',
        backgroundColor: '#f9fcfe',
        border: '1px solid #dce8f0',
        color: '#37474f',
        lineHeight: 1.45,
        fontSize: '11px',
        '& label': {
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            margin: 0,
            cursor: 'pointer',
        },
        '& input[type="checkbox"]': {
            width: 17,
            height: 17,
            marginTop: 2,
            flex: '0 0 auto',
        },
    },
    termsLink: {
        border: 0,
        background: 'transparent',
        color: '#2f6fb0',
        cursor: 'pointer',
        fontWeight: 700,
        padding: 0,
        textDecoration: 'underline',
    },
    paymentInstructionsLinkContainer: {
        padding: '18px 0 8px 0',
        textAlign: 'center',
    },
    paymentInstructionsLink: {
        border: '1px solid #b8d9ea',
        background: '#f3f9fc',
        borderRadius: '999px',
        color: '#236d93',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 700,
        padding: '9px 16px',
        textDecoration: 'none',
        boxShadow: '0 6px 16px rgba(38, 50, 56, 0.06)',
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': {
            background: '#e9f5fa',
            borderColor: '#8ec3dc',
        },
    },
    dialogPaper: {
        borderRadius: '8px',
        overflow: 'hidden',
        maxWidth: '920px',
    },
    dialogTitle: {
        backgroundColor: '#f6fafc',
        borderBottom: '1px solid #d9e6ef',
        color: '#263238',
        padding: '18px 24px',
        '& h2': {
            fontSize: '17px',
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: 0,
        },
    },
    dialogActions: {
        padding: '10px 20px 14px',
        borderTop: '1px solid #eef3f6',
        '& button': {
            fontSize: '13px',
            fontWeight: 700,
        },
    },
    paymentInstructionsContent: {
        color: '#37474f',
        fontSize: '13px',
        padding: '18px 24px',
        '& h3': {
            margin: '14px 0 7px 0',
            fontSize: '13px',
            lineHeight: 1.35,
            color: '#263238',
            letterSpacing: 0,
            textTransform: 'uppercase',
        },
        '& ul': {
            margin: '0 0 12px 18px',
            padding: 0,
        },
        '& li': {
            margin: '6px 0',
            lineHeight: 1.55,
            textAlign: 'left',
        },
        '& h5': {
            display: 'inline',
            margin: 0,
            fontSize: '13px',
            fontWeight: 400,
            lineHeight: 1.55,
        },
    },
    paymentInstructionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '12px 28px',
    },
    paymentInstructionsReminder: {
        marginTop: '14px',
        padding: '12px 14px',
        borderRadius: '6px',
        backgroundColor: '#eef7fb',
        border: '1px solid #c9e4f1',
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.45,
        color: '#263238',
        fontSize: '12px',
    },
    termsContent: {
        color: '#37474f',
        fontSize: '13px',
        padding: '18px 24px',
        '& p': {
            margin: '7px 0 0 0',
            lineHeight: 1.55,
            textAlign: 'left',
            color: '#37474f',
        },
        '& h4': {
            margin: '14px 0 5px 0',
            color: '#263238',
            fontSize: '13px',
            fontWeight: 700,
            lineHeight: 1.4,
            textTransform: 'uppercase',
        },
        '& ul': {
            margin: '7px 0 7px 18px',
            padding: 0,
        },
        '& li': {
            margin: '5px 0',
            lineHeight: 1.55,
            textAlign: 'left',
        },
    },
    historyContainer: {
        marginTop: '20px',
    },
    paymentReturnCard: {
        margin: '16px auto 0',
        border: '1px solid #d7e2ea',
        borderLeft: '4px solid #3C8DBC',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        boxShadow: 'none',
    },
    paymentReturnApproved: {
        borderColor: '#b9ddbc',
        borderLeftColor: '#2e7d32',
        backgroundColor: '#fbfefb',
        '& $paymentReturnBadge': {
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
        },
        '& $paymentReturnHeader, & $paymentReturnActions': {
            backgroundColor: '#f3faf4',
            borderColor: '#dcefe0',
        },
        '& $paymentReturnItem': {
            borderColor: '#dcefe0',
        },
    },
    paymentReturnRejected: {
        borderColor: '#efc2c2',
        borderLeftColor: '#c62828',
        backgroundColor: '#fffafa',
        '& $paymentReturnBadge': {
            backgroundColor: '#ffebee',
            color: '#c62828',
        },
        '& $paymentReturnHeader, & $paymentReturnActions': {
            backgroundColor: '#fff5f5',
            borderColor: '#f5dddd',
        },
        '& $paymentReturnItem': {
            borderColor: '#f5dddd',
        },
    },
    paymentReturnPending: {
        borderColor: '#f0cfa8',
        borderLeftColor: '#ef6c00',
        backgroundColor: '#fffdf9',
        '& $paymentReturnBadge': {
            backgroundColor: '#fff3e0',
            color: '#e56a00',
        },
        '& $paymentReturnHeader, & $paymentReturnActions': {
            backgroundColor: '#fff8ef',
            borderColor: '#f4dfc5',
        },
        '& $paymentReturnItem': {
            borderColor: '#f4dfc5',
        },
    },
    paymentReturnHeader: {
        padding: '10px 16px',
        fontWeight: 700,
        fontSize: '16px',
        color: '#37474f',
        borderBottom: '1px solid #edf2f7',
        backgroundColor: '#fbfdff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    paymentReturnBadge: {
        padding: '4px 9px',
        borderRadius: '999px',
        backgroundColor: '#fff3e0',
        color: '#ef6c00',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0,
    },
    paymentReturnGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.8fr) minmax(130px, 0.65fr) minmax(130px, 0.65fr)',
        padding: '12px 16px',
    },
    paymentReturnItem: {
        minWidth: 0,
        padding: '1px 14px',
        borderLeft: '1px solid #e3ebf0',
        '&:first-child': {
            paddingLeft: 0,
            borderLeft: 0,
        },
        '& span': {
            display: 'block',
            color: '#607d8b',
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: '3px',
        },
        '& strong': {
            color: '#37474f',
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: 1.4,
            overflowWrap: 'anywhere',
        },
    },
    paymentReturnReference: {
        fontFamily: 'inherit',
        fontSize: '14px !important',
        fontWeight: 500,
        letterSpacing: 0,
    },
    paymentReturnActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '6px 12px',
        borderTop: '1px solid #edf2f7',
        backgroundColor: '#fbfdff',
        textAlign: 'right',
        '& button': {
            fontSize: '11px',
            fontWeight: 700,
            minWidth: 0,
            padding: '4px 8px',
        },
    },
    '@media (max-width: 700px)': {
        paymentReturnHeader: {
            fontSize: '15px',
        },
        paymentReturnGrid: {
            gridTemplateColumns: '1fr',
            gap: '10px',
        },
        paymentReturnItem: {
            padding: '0 0 9px',
            borderLeft: 0,
            borderBottom: '1px solid #e3ebf0',
            '&:last-child': {
                paddingBottom: 0,
                borderBottom: 0,
            },
        },
    },
    searchLoader: {
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        color: '#3C8DBC',
        fontWeight: 600,
    },
    tablePaper: {
        borderRadius: '8px',
        border: '1px solid #d9e6ef',
        boxShadow: 'none',
        overflowX: 'auto',
    },
    emptyState: {
        textAlign: 'center',
        padding: '42px 20px',
        color: '#2e7d32',
        backgroundColor: '#f6fbf6',
        borderRadius: '8px',
        border: '1px solid #d5ead6',
        '& h3': {
            margin: '0 0 8px',
            fontSize: '17px',
            lineHeight: 1.35,
        },
        '& p': {
            margin: 0,
            color: '#4c6b4d',
            fontSize: '12px',
        },
    },
    paymentError: {
        marginTop: '12px',
        padding: '12px 14px',
        borderRadius: '6px',
        border: '1px solid #ffcdd2',
        backgroundColor: '#fff5f5',
        color: '#b71c1c',
        lineHeight: 1.5,
        fontWeight: 600,
    },
    paymentActions: {
        marginTop: '18px',
        textAlign: 'right',
        '& button': {
            borderRadius: '6px',
            padding: '10px 18px',
            fontWeight: 700,
            fontSize: '12px',
            backgroundColor: '#3147b7',
            boxShadow: '0 10px 22px rgba(49, 71, 183, 0.24)',
        },
        '& button:hover': {
            backgroundColor: '#26389b',
        },
    },
});

// Lista de formularios disponibles con sus siglas
const formularios = [
    { id: 1, nombre: 'ANT cambio de socio y vehiculo.pdf', sigla: 'SCSV', archivo: 'ANT cambio de socio y vehiculo.pdf' },
    { id: 2, nombre: 'ANT cambio de vehiculo.pdf', sigla: 'SCV', archivo: 'ANT cambio de vehiculo.pdf' },
    { id: 3, nombre: 'ANT cambio socio.pdf', sigla: 'SCS', archivo: 'ANT cambio socio.pdf' },
    { id: 4, nombre: 'ANT constitucion juridica.pdf', sigla: 'SCJ', archivo: 'ANT constitucion juridica.pdf' },
    { id: 5, nombre: 'ANT deshabilitacion de vehiculo.pdf', sigla: 'SDV', archivo: 'ANT deshabilitacion de vehiculo.pdf' },
    { id: 6, nombre: 'ANT habilitacion de vehiculo.pdf', sigla: 'SHV', archivo: 'ANT habilitacion de vehiculo.pdf' },
    { id: 7, nombre: 'ANT permiso de operación.pdf', sigla: 'SCPO', archivo: 'ANT permiso de operacion.pdf' },
    { id: 8, nombre: 'ANT renovacion permisos operacion.pdf', sigla: 'SRPO', archivo: 'ANT renovacion permisos operacion.pdf' },
    { id: 9, nombre: 'Aviso Alcabalas.pdf', sigla: 'AVA', archivo: 'Aviso Alcabalas.pdf' },
    { id: 10, nombre: 'Certificado de no adeudar.pdf', sigla: 'CDNA', archivo: 'Certificado de no adeudar.pdf' },
    { id: 11, nombre: 'Declaracion Activos totales.pdf', sigla: 'SDUM', archivo: 'Declaracion Activos totales.pdf' },
    { id: 12, nombre: 'Declaracion Plusvalia.pdf', sigla: 'SDP', archivo: 'DeclaracionPlusvalia.pdf' },
    { id: 13, nombre: 'Solicitud aprobacion anteproyectos.pdf', sigla: 'SAP', archivo: 'S aprobacion anteproyectos.pdf' },
    { id: 14, nombre: 'Solicitud aprobacion de planos arquitectonicos.pdf', sigla: 'SAPA', archivo: 'S aprobacion de planos arquitectonicos.pdf' },
    { id: 15, nombre: 'Solicitud aprobacion fraccionamientos.pdf', sigla: 'SPF', archivo: 'S aprobacion fraccionamientos.pdf' },
    { id: 16, nombre: 'Solicitud Avaluos y Catastros.pdf', sigla: 'SAC', archivo: 'S Avaluos y Catastros.pdf' },
    { id: 17, nombre: 'Solicitud de reclamos.pdf', sigla: 'FDR', archivo: 'SReclamos.pdf' },
    { id: 18, nombre: 'Solicitud Facilidades de Pago.pdf', sigla: 'SFP', archivo: 'Solicitud Facilidades de Pago.pdf' },
    { id: 19, nombre: 'Solicitud General.pdf', sigla: 'STT', archivo: 'Solicitud General.pdf' },
    { id: 20, nombre: 'Solicitud linea de fabrica.pdf', sigla: 'SLF', archivo: 'S linea de fabrica.pdf' },
    { id: 21, nombre: 'Solicitud permiso de construccion.pdf', sigla: 'SPC', archivo: 'S permiso de construccion.pdf' },
    { id: 22, nombre: 'Solicitud realizacion inspecciones.pdf', sigla: 'SRI', archivo: 'S realizacion inspecciones.pdf' },
    { id: 23, nombre: 'Solicitud servicios cementerios.pdf', sigla: 'SSC', archivo: 'S servicios cementerios.pdf' },
    { id: 24, nombre: 'Solicitud trabajos varios.pdf', sigla: 'STV', archivo: 'S trabajos varios.pdf' },
];

const emptyBuyer = {
    documentType: 'CI',
    document: '',
    name: '',
    surname: '',
    email: '',
    mobile: '',
};

const LAST_PAYMENT_IDENTIFICATION_KEY = 'placetopay_last_identificacion';
const ACTIVE_PAYMENT_REFERENCE_KEY = 'placetopay_active_reference';
const PAYMENT_RETURN_PENDING_KEY = 'placetopay_return_pending';
const PAYMENT_RETURN_RETRY_ATTEMPTS = 3;
const PAYMENT_RETURN_RETRY_DELAY_MS = 1500;
const RUC_SURNAME_PLACEHOLDER = '.';

const termsSections = [
    {
        title: '1.- ACEPTACIÓN DEL SERVICIO "BOTÓN DE PAGOS"',
        body: [
            'El Municipio del GOBIERNO AUTONOMO DESCENTRALIZADO MUNICIPAL DEL CANTON PEDRO VICENTE MALDONADO pone a disposición de los usuarios el canal de recaudación Botón de Pagos a través de su página Web el mismo que permite realizar las consultadas y/o pagos de sus títulos emitidos por concepto de impuestos, tasas y contribuciones.',
            'El servicio de recaudación a través de este canal será prestado por el Municipio del CANTON PEDRO VICENTE MALDONADO, en adelante el "PRESTADOR" que será brindado a usted usuario en adelante "CLIENTE" bajo los términos y condiciones previstos en el presente contrato.',
            'Al ingresar y usar este sitio Web el "CLIENTE" expresa su voluntad y acepta los términos y condiciones establecidos pulsando sobre el recuadro "ACEPTO", si el "CLIENTE" NO está de acuerdo con estos términos y condiciones no acceda o haga uso de este sitio, por cuanto se entiende que existe un acuerdo en común entre las partes tanto del "PRESTADOR" como por parte del "CLIENTE" para realizar sus consultas y/o pagos de sus títulos de los impuestos, tasas y contribuciones.',
        ],
    },
    {
        title: '2.- REGISTRO POR PARTE DEL CLIENTE',
        body: [
            'Próximamente el PRESTADOR habilitará este registro para que el CLIENTE pueda acceder a las consultas y/o pagos de sus impuestos tasas y contribuciones a través de la página Web que el "PRESTADOR" pone a su disposición deberá registrarse y proporcionar ciertos datos personales que serán solicitados a fin de poder suministrarle nuestros servicios, el "CLIENTE" brindará toda la información verdadera, precisa y completa, esta información provista por el "CLIENTE" se actualizará a la brevedad.',
            'En caso de que el \'\'CLIENTE\'\' proporcione información falsa, imprecisa o incompleta el "PRESTADOR" podrá dar por concluido este contrato.',
            'El "CLIENTE" al momento de finalizar el registro se le proporcionará un nombre de inicio de sesión y contraseña y será el responsable de lo que suceda con su nombre de inicio de sesión y contraseña puesto que es el responsable del manejo de su clave y nombre de usuario.',
        ],
    },
    {
        title: '3.- MODIFICACIONES',
        body: 'El "PRESTADOR" podrá calificar cuantas veces sea necesario los términos y condiciones establecidas en este contrato con el fin de aclararlos sin necesidad de formalidad alguna, cada vez que el "PRESTADOR" realice un cambio a estos términos y condiciones serán informados en la página principal debiendo el cliente pulsar sobre el recuadro "ACEPTO" si el cliente no pulsara sobre el recuadro "ACEPTO" se dará por concluido en ese mismo momento, sin incurrir por ello en responsabilidad alguna para el "PRESTADOR".',
    },
    {
        title: '4.- COSTOS',
        body: 'El "PRESTADOR" en la actualidad ofrece este servicio de manera gratuita, sin que ello limite en un futuro a su modificación, la misma que será notificada a través de esta página Web.',
    },
    {
        title: '5.- SERVICIOS',
        body: 'Los servicios disponibles son los que se encuentran habilitados al momento de su acceso en el sitio Web disponible por el "PRESTADOR".',
    },
    {
        title: '6.- CONFIDENCIALlDAD',
        body: 'La utilización del canal de recaudación Botón de Pagos que el "PRESTADOR" pone a disposición del \'\'CLIENTE\'\' es de exclusiva responsabilidad del usuario la cual se considera como información privada. El "PRESTADOR" no se hace responsable por el mal manejo de la información proporcionada al "CLIENTE".',
    },
    {
        title: '7.- POLÍTICA DE PRIVACIDAD',
        body: 'Por la prestación de este servicio, el "PRESTADOR" podrá recopilar información de registro, información que pasará a terceros cuando ésta sea requerida por la ley o por acciones legales para las cuales ésta información es relevante, como cuando se trate de una orden judicial o a propósito para prevenir un delito o fraude. En cuyo caso se entenderá que el "CLIENTE" ha dado su permiso para revelar la información constante por la ejecución del servicio.',
    },
    {
        title: '8.- PROPIEDAD',
        body: 'El "CLIENTE" acepta que el "PRESTADOR" es el dueño y propietario de los derechos personales y reales sobre la Base de Datos que se proporcionará en este servicio de recaudación.',
    },
    {
        title: '9.- TERMINACIÓN',
        body: [
            'El "CLIENTE" tiene el derecho de cancelar o finalizar la utilización de este servicio de pagos de sus títulos emitidos por concepto de impuestos, tasas y contribuciones en cualquier momento.',
            'Una vez cancelado el servicio, el "CLIENTE" no tendrá derecho ni podrá seguir utilizando este canal de recaudación, pero podrá utilizar todos los canales de recaudación que dispone el PRESTADOR El servicio de recaudación a través de la página del Municipio dewww.pedrovicentemaldonado.gob.ec“Botón de Pagos” estará disponible de forma permanente desde las 7:00 hasta las 24:00, los 7 días de la semana, durante los 365 días del año.',
            'HORARIO DE SERVICIO: Lunes a Domingo De 8:00 a 20:00',
            'Aclaratoria: El prestador comunicará con 48 horas de anticipación la interrupción del servicio cuando sea necesario realizar las operaciones de mantenimiento.',
        ],
    },
    {
        title: '10.- INDEMNIDAD',
        body: 'El "CLIENTE" se obliga a mantener indemnes de todo reclamo o demanda a el "PRESTADOR", sus accionistas, funcionarios, agentes y empleados, incluyendo honorarios de abogados, hincado por algún tercero debido a, o que surja como consecuencia del uso por parte suya del servicio que se le ha proporcionado, de su violación de los términos y condiciones, o de la violación de algún derecho de un tercero.',
    },
    {
        title: '11.- COMERCIAL',
        body: 'El "CLIENTE" tiene derecho a usar el servido de consultas y/o pagos de sus títulos como un servido personal, el "CLIENTE" se obliga además a utilizar responsablemente cada uno de los servicios habilitados.',
    },
    {
        title: '12.- PROHIBICIONES',
        body: [
            'El "CLIENTE" se obliga a no realizar cualquiera de los siguientes actos:',
            'Fingir ser una persona;',
            'Violar alguna ley nacional a través del, o en el servicio;',
            'Causar molestias o perturbar a terceros a través del, o en el servicio;',
            'Recopilar o almacenar datos de terceros o acerca de ellos;',
            'Utilizar el servido de alguna forma que no sea lo expresamente permitido;',
            'Reproducir, duplicar, copiar vender o explotar con algún fin comercial alguna parte que por uso o acceso se tiene al servicio;',
            'Utilizar términos o expresiones injuriosas, intimidantes, calumniantes o contrarias a las buenas costumbres.',
        ],
    },
    {
        title: '13.- JURISDICCIÓN Y COMPETENCIA',
        body: 'La legislación aplicable a este contrato es la ecuatoriana. Para el caso de reclamo con respecto a la relación o ejecución en cumplimiento de las condiciones establecidas en este documento o de la utilización del servido, incluyendo sin limitación, el reclamo sobre la validez, interpretación, exigibilidad o incumplimiento de dichas condiciones, finalmente será resuelto por los jueces competentes del cantón Pedro Vicente Maldonado, provincia de Pichincha.',
    },
];

const PRIVACY_DOCUMENT_TAG = 'PRIVACY_POLICY';

const privacyPolicyFallback = {
    title: 'CONSENTIMIENTO PARA EL TRATAMIENTO DE DATOS PERSONALES',
    content: `Con el fin de brindar y garantizar una atención eficiente, personalizada y de calidad a la ciudadanía, usuarios, proveedores y demás personas que interactúan con el Gobierno Autónomo Descentralizado Municipal del Cantón Pedro Vicente Maldonado (en adelante, el “Responsable”), este podrá solicitar, recopilar, registrar, procesar, almacenar, actualizar y administrar información personal no sensible de los titulares de los datos (en adelante, el “Titular”), conforme a lo establecido en la Ley Orgánica de Protección de Datos Personales y su normativa aplicable.

Los datos personales proporcionados por el Titular serán tratados con la finalidad de gestionar trámites administrativos, prestar servicios municipales, mantener comunicaciones institucionales presenciales y digitales, atender requerimientos ciudadanos, gestionar procesos de participación ciudadana, realizar notificaciones relacionadas con los servicios municipales, ejecutar procesos administrativos, financieros, tributarios y de contratación pública, así como cumplir con las competencias y obligaciones legales que corresponden al Gobierno Autónomo Descentralizado Municipal del Cantón Pedro Vicente Maldonado (en adelante, la “Finalidad”).

El Responsable deja constancia de que los datos personales recopilados no serán utilizados para fines distintos de aquellos establecidos en el presente Consentimiento, salvo que exista autorización expresa del Titular o disposición legal que lo permita.

Asimismo, se informa al Titular que el Responsable podrá compartir la información con dependencias institucionales, organismos de control, entidades públicas competentes o proveedores de servicios contratados que intervengan en el cumplimiento de la Finalidad, quienes deberán cumplir con las mismas obligaciones de confidencialidad, seguridad y protección de datos personales asumidas por el Responsable. En ningún caso la información será entregada a terceros para fines distintos a los establecidos en este documento, salvo requerimiento expreso de autoridad competente en ejercicio de sus atribuciones legales.

Los datos personales serán conservados durante el tiempo necesario para cumplir con las finalidades descritas, las obligaciones legales, reglamentarias y de archivo institucional que correspondan al Gobierno Autónomo Descentralizado Municipal de Pablo Sexto, de conformidad con la normativa vigente.

El Titular podrá revocar su consentimiento en cualquier momento, sin perjuicio de las obligaciones legales que requieran la conservación de determinada información por parte de la institución.

De conformidad con la Ley Orgánica de Protección de Datos Personales, el Titular tiene derecho a:

Solicitar el acceso a sus datos personales.

Solicitar la rectificación y actualización de sus datos personales cuando estos sean inexactos o incompletos.

Solicitar la eliminación de sus datos personales, cuando proceda legalmente.

Solicitar la suspensión o limitación del tratamiento de sus datos.

Retirar el consentimiento otorgado para el tratamiento de sus datos personales.

Oponerse al tratamiento de sus datos personales en los casos previstos por la ley.

Presentar reclamaciones ante la autoridad competente cuando considere vulnerados sus derechos.

Para ejercer cualquiera de los derechos antes señalados, el Titular podrá presentar su solicitud por escrito ante la Secretaría General del Gobierno Autónomo Descentralizado Municipal del Cantón Pedro Vicente Maldonado o remitirla al correo electrónico institucional secretariageneral@pedrovicentemaldonado.gob.ec

La solicitud será atendida dentro de los plazos establecidos en la Ley Orgánica de Protección de Datos Personales y demás normativa aplicable.`,
};

const privacyPolicyListItems = [
    'Solicitar el acceso a sus datos personales.',
    'Solicitar la rectificación y actualización de sus datos personales cuando estos sean inexactos o incompletos.',
    'Solicitar la eliminación de sus datos personales, cuando proceda legalmente.',
    'Solicitar la suspensión o limitación del tratamiento de sus datos.',
    'Retirar el consentimiento otorgado para el tratamiento de sus datos personales.',
    'Oponerse al tratamiento de sus datos personales en los casos previstos por la ley.',
    'Presentar reclamaciones ante la autoridad competente cuando considere vulnerados sus derechos.',
];

function getTrcId(row) {
    const id = Number(row.trcId || row.trc_id || row.trcID);
    return Number.isFinite(id) ? id : null;
}

function getNumber(row, ...keys) {
    for (const key of keys) {
        const value = Number(row[key]);
        if (Number.isFinite(value)) return value;
    }
    return 0;
}

function getRowTotal(row) {
    return getNumber(row, 'trcValorEmitido', 'trcTotalCobro', 'trc_total_cobro');
}

function getRowIva(row) {
    return getNumber(row, 'trcIva', 'trc_iva');
}

function calculateDebtTotal(rows) {
    return (rows || []).reduce((total, row) => total + getRowTotal(row), 0);
}

function isValidEmail(value) {
    const email = (value || '').trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function isValidEcuadorMobile(value) {
    const mobile = (value || '').trim();
    return /^09\d{8}$/.test(mobile);
}

function isCashierChargeGenerated(payment) {
    const value = payment?.cashierChargeGenerated ?? payment?.cobroGenerado ?? 0;
    return value === true || Number(value) === 1 || String(value).toLowerCase() === 'true';
}

function parseCashierChargeIds(value) {
    if (Array.isArray(value)) {
        return value.map(Number).filter(Number.isFinite);
    }

    if (value === null || typeof value === 'undefined' || value === '') {
        return [];
    }

    const text = String(value).trim();
    if (!text) return [];

    try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
            return parsed.map(Number).filter(Number.isFinite);
        }
    } catch (_error) {
        // El backend puede enviar "[1,2]" o texto plano; abajo queda el respaldo.
    }

    return text
        .replace(/\[/g, '')
        .replace(/\]/g, '')
        .replace(/\s/g, '')
        .split(',')
        .map(Number)
        .filter(Number.isFinite);
}

function getPaidTitleIdsFromHistory(historyRows) {
    const paidIds = new Set();

    (historyRows || []).forEach(payment => {
        if (!isApprovedPayment(payment.status) || !isCashierChargeGenerated(payment)) return;
        parseCashierChargeIds(payment.cashierChargeIds ?? payment.cobroIds ?? payment.trcIds)
            .forEach(id => paidIds.add(id));
    });

    return paidIds;
}

function filterPaidRows(rows, paidIds) {
    if (!paidIds || paidIds.size === 0) return rows || [];
    return (rows || []).filter(row => {
        const trcId = getTrcId(row);
        return trcId === null || !paidIds.has(trcId);
    });
}

function splitName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
        return { name: parts[0] || '', surname: '' };
    }
    return {
        name: parts.slice(0, Math.max(1, parts.length - 2)).join(' ') || parts[0],
        surname: parts.slice(-2).join(' '),
    };
}

function onlyDigits(value) {
    return String(value || '').replace(/\D/g, '');
}

function isRucIdentification(value) {
    return onlyDigits(value).length === 13;
}

function getDocumentTypeByIdentification(value) {
    return isRucIdentification(value) ? 'RUC' : 'CI';
}

function getFirstNonEmpty(row, fields) {
    const normalizedRow = Object.keys(row || {}).reduce((values, key) => {
        values[key.toLowerCase()] = row[key];
        return values;
    }, {});

    for (const field of fields) {
        const value = row?.[field] ?? normalizedRow[field.toLowerCase()];
        if (value !== undefined && value !== null && String(value).trim()) {
            return String(value).trim();
        }
    }

    return '';
}

function getBusinessName(row) {
    return getFirstNonEmpty(row, [
        'razonSocial',
        'razon_social',
        'proRazonSocial',
        'pro_razon_social',
        'nombreComercial',
        'nombre_comercial',
        'contribuyente',
        'contribuyenteNombre',
        'trcContribuyenteNombre',
        'propietario',
        'nombrePropietario',
        'proNombreCompleto',
    ]) || `${row?.proApellido || ''} ${row?.proNombre || ''}`.trim();
}

function paymentStatusLabel(status) {
    const normalized = String(status || '').toUpperCase();
    if (normalized === 'APPROVED') return 'Aprobado';
    if (normalized === 'REJECTED' || normalized === 'FAILED'
        || normalized === 'EXPIRED' || normalized === 'CANCELLED') return 'Rechazado';
    return 'Pendiente';
}

function paymentReturnTitle(status) {
    const label = paymentStatusLabel(status);
    if (label === 'Aprobado') return 'Transacción aprobada';
    if (label === 'Rechazado') return 'Transacción rechazada';
    return 'Transacción pendiente';
}

function paymentReturnClass(status, classes) {
    const label = paymentStatusLabel(status);
    if (label === 'Aprobado') return `${classes.paymentReturnCard} ${classes.paymentReturnApproved}`;
    if (label === 'Rechazado') return `${classes.paymentReturnCard} ${classes.paymentReturnRejected}`;
    return `${classes.paymentReturnCard} ${classes.paymentReturnPending}`;
}

function formatPaymentAmount(payment) {
    const value = Number(payment && (payment.gatewayTotal || payment.total));
    if (Number.isNaN(value)) return payment && payment.total ? `${payment.currency || 'USD'} ${payment.total}` : '-';
    return `${payment.currency || 'USD'} ${value.toFixed(2)}`;
}

function formatCurrency(value, currency = '$') {
    const number = Number(value);
    if (!Number.isFinite(number)) return `${currency}0.00`;
    return `${currency}${number.toFixed(2)}`;
}

function isApprovedPayment(status) {
    return String(status || '').toUpperCase() === 'APPROVED';
}

function isPendingPayment(status) {
    return String(status || '').toUpperCase() === 'PENDING';
}

function waitPaymentReturnRetry() {
    return new Promise(resolve => {
        window.setTimeout(resolve, PAYMENT_RETURN_RETRY_DELAY_MS);
    });
}

function formatPaymentDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('es-EC', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
}

function renderPrivacyPolicyContent(content) {
    const paragraphs = String(content || '').split(/\n{2,}/);
    const elements = [];
    let listItems = [];

    const flushList = () => {
        if (!listItems.length) return;
        elements.push(
            <ul key={`privacy-policy-list-${elements.length}`}>
                {listItems.map(item => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        );
        listItems = [];
    };

    paragraphs.forEach((paragraph) => {
        const text = paragraph.trim();
        if (!text) return;

        if (privacyPolicyListItems.includes(text)) {
            listItems.push(text);
            return;
        }

        flushList();
        elements.push(
            <p key={`privacy-policy-${elements.length}`}>
                {text}
            </p>
        );
    });

    flushList();
    return elements;
}

export default function ConsultaDeuda() {
    const classes = useStyles();
    const [showTable, setShowTable] = React.useState(false);
    const [data, setData] = React.useState(null);
    const [toPay, setToPay] = React.useState(0);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedFormulario, setSelectedFormulario] = React.useState(null);
    const [isPrinting, setIsPrinting] = React.useState(false);
    const [identificacion, setIdentificacion] = React.useState(''); // NUEVO: Guardar identificación
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [buyer, setBuyer] = React.useState(emptyBuyer);
    const [acceptedTerms, setAcceptedTerms] = React.useState(false);
    const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = React.useState(false);
    const [isPaying, setIsPaying] = React.useState(false);
    const [isRedirectingToPayment, setIsRedirectingToPayment] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);
    const [paymentHistory, setPaymentHistory] = React.useState([]);
    const [paymentError, setPaymentError] = React.useState('');
    const [paymentHistoryError, setPaymentHistoryError] = React.useState('');
    const [paymentReturnMessage, setPaymentReturnMessage] = React.useState('');
    const [paymentReturnSummary, setPaymentReturnSummary] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [historyPage, setHistoryPage] = React.useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = React.useState(5);
    const [showTerms, setShowTerms] = React.useState(false);
    const [showPrivacyPolicy, setShowPrivacyPolicy] = React.useState(false);
    const [showPaymentInstructions, setShowPaymentInstructions] = React.useState(false);
    const [privacyPolicy, setPrivacyPolicy] = React.useState(privacyPolicyFallback);
    const paidTitleIdsRef = React.useRef(new Set());

    const payableRows = React.useMemo(() => (data || []).filter(row => getTrcId(row) !== null), [data]);
    const paginatedRows = React.useMemo(() => {
        if (!data) return [];
        const start = page * rowsPerPage;
        return data.slice(start, start + rowsPerPage);
    }, [data, page, rowsPerPage]);
    const paginatedPaymentHistory = React.useMemo(() => {
        const start = historyPage * historyRowsPerPage;
        return paymentHistory.slice(start, start + historyRowsPerPage);
    }, [paymentHistory, historyPage, historyRowsPerPage]);
    const selectedRows = React.useMemo(
        () => (data || []).filter(row => {
            const trcId = getTrcId(row);
            return trcId !== null && selectedIds.includes(trcId);
        }),
        [data, selectedIds]
    );
    const selectedTotal = selectedRows.reduce((total, row) => total + getRowTotal(row), 0);
    const selectedIva = selectedRows.reduce((total, row) => total + getRowIva(row), 0);
    const selectedSubtotal = Math.max(0, selectedTotal - selectedIva);
    const buyerIsRuc = buyer.documentType === 'RUC';
    const buyerDocumentType = buyerIsRuc ? 'RUC' : buyer.documentType;
    const buyerValidationErrors = getBuyerValidationErrors(buyer);
    const buyerEmailInvalid = Boolean(buyer.email.trim()) && !isValidEmail(buyer.email);
    const buyerMobileInvalid = Boolean(buyer.mobile.trim()) && !isValidEcuadorMobile(buyer.mobile);
    const canPay = selectedIds.length > 0
        && acceptedTerms
        && acceptedPrivacyPolicy
        && buyerDocumentType.trim()
        && buyer.document.trim()
        && buyer.name.trim()
        && (buyerIsRuc || buyer.surname.trim())
        && buyer.email.trim()
        && buyer.mobile.trim()
        && Object.keys(buyerValidationErrors).length === 0
        && !buyerEmailInvalid
        && !buyerMobileInvalid;

    React.useEffect(() => {
        setPage(0);
    }, [data]);

    React.useEffect(() => {
        setHistoryPage(0);
    }, [paymentHistory]);

    React.useEffect(() => {
        GeneralService.obtenerDocumentoPlacetopay(PRIVACY_DOCUMENT_TAG).then(result => {
            const document = result.data || {};
            setPrivacyPolicy({
                title: document.title || privacyPolicyFallback.title,
                content: document.content || privacyPolicyFallback.content,
            });
        }, (error) => {
            console.warn('No se pudo cargar la política de privacidad desde base de datos', error);
            setPrivacyPolicy(privacyPolicyFallback);
        });
    }, []);

    const removePaidTitlesFromTable = React.useCallback((historyRows) => {
        const paidIds = getPaidTitleIdsFromHistory(historyRows);
        if (paidIds.size === 0) return;

        paidIds.forEach(id => paidTitleIdsRef.current.add(id));
        setSelectedIds(current => current.filter(id => !paidIds.has(id)));
        setData(current => {
            if (!Array.isArray(current)) return current;
            const filteredRows = filterPaidRows(current, paidIds);
            setToPay(calculateDebtTotal(filteredRows));
            return filteredRows;
        });
    }, []);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleFormularioSelect = (formulario) => {
        setSelectedFormulario(formulario);
        handleMenuClose();
    };

    const handleBuyerChange = (field, value) => {
        setBuyer(current => {
            const nextValue = field === 'mobile'
                ? value.replace(/\D/g, '').slice(0, 10)
                : sanitizeBuyerField(field, value, current.documentType);
            const next = {
                ...current,
                [field]: nextValue,
            };

            if (field === 'document' && current.documentType !== 'PAS') {
                next.documentType = getDocumentTypeByIdentification(nextValue);
            }

            if (field === 'documentType' && value === 'RUC' && !next.surname.trim()) {
                next.surname = RUC_SURNAME_PLACEHOLDER;
            }

            if (field === 'name' && next.documentType === 'RUC' && !next.surname.trim()) {
                next.surname = RUC_SURNAME_PLACEHOLDER;
            }

            return next;
        });
    };

    const toggleSelection = (row) => {
        const trcId = getTrcId(row);
        if (trcId === null) return;

        setSelectedIds(current => (
            current.includes(trcId)
                ? current.filter(id => id !== trcId)
                : [...current, trcId]
        ));
    };

    const toggleAllSelection = () => {
        if (selectedIds.length === payableRows.length) {
            setSelectedIds([]);
            return;
        }
        setSelectedIds(payableRows.map(row => getTrcId(row)).filter(id => id !== null));
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeHistoryPage = (_event, newPage) => {
        setHistoryPage(newPage);
    };

    const handleChangeHistoryRowsPerPage = (event) => {
        setHistoryRowsPerPage(parseInt(event.target.value, 10));
        setHistoryPage(0);
    };

    const clearPaymentReturnSummary = () => {
        setPaymentReturnSummary(null);
        setPaymentReturnMessage('');
        window.sessionStorage.removeItem(ACTIVE_PAYMENT_REFERENCE_KEY);
    };

    const cargarHistorialPlacetopay = React.useCallback((credentialValue) => {
        if (!credentialValue) return;

        setPaymentHistoryError('');
        GeneralService.buscarHistorialPlacetopay(credentialValue).then(result => {
            const historyRows = Array.isArray(result.data) ? result.data : [];
            setPaymentHistory(historyRows);
            removePaidTitlesFromTable(historyRows);
        }, (error) => {
            console.error('Error al cargar historial Placetopay', error);
            setPaymentHistory([]);
            setPaymentHistoryError('No se pudo cargar el historial de transacciones Placetopay.');
        });
    }, [removePaidTitlesFromTable]);

    const handlePay = async () => {
        if (!canPay || isPaying || isRedirectingToPayment) return;

        setIsPaying(true);
        setPaymentError('');
        let shouldRedirect = false;

        try {
            const firstPaymentRow = selectedRows[0] || payableRows[0] || (data || [])[0];
            const businessNameForPayment = buyerIsRuc
                ? (buyer.name.trim() || getBusinessName(firstPaymentRow))
                : '';
            const buyerNameForPayment = buyerIsRuc ? businessNameForPayment : buyer.name.trim();
            const buyerSurnameForPayment = buyerIsRuc
                ? (businessNameForPayment || RUC_SURNAME_PLACEHOLDER)
                : buyer.surname.trim();
            let ipAddress = '127.0.0.1';
            try {
                const ipResponse = await GeneralService.obtenerIpCliente();
                ipAddress = (ipResponse.data && ipResponse.data.ipAddress) || ipAddress;
            } catch (error) {
                console.warn('No se pudo obtener la IP del cliente', error);
            }

            const response = await GeneralService.crearSesionPlacetopay({
                identificacion: identificacion.trim(),
                trcIds: selectedIds,
                ipAddress,
                comprador: {
                    documentType: buyerDocumentType.trim(),
                    document: buyer.document.trim(),
                    name: buyerNameForPayment,
                    surname: buyerSurnameForPayment,
                    nombres: buyerNameForPayment,
                    apellidos: buyerSurnameForPayment,
                    razonSocial: buyerIsRuc ? businessNameForPayment : '',
                    company: buyerIsRuc ? businessNameForPayment : '',
                    email: buyer.email.trim(),
                    mobile: buyer.mobile.trim(),
                },
            });

            if (!response.data || !response.data.processUrl) {
                setPaymentError('No se recibió la URL de pago de Placetopay.');
                return;
            }

            window.localStorage.setItem(LAST_PAYMENT_IDENTIFICATION_KEY, identificacion.trim());
            if (response.data.reference) {
                window.sessionStorage.setItem(ACTIVE_PAYMENT_REFERENCE_KEY, response.data.reference);
            }
            window.sessionStorage.setItem(PAYMENT_RETURN_PENDING_KEY, '1');

            shouldRedirect = true;
            setIsRedirectingToPayment(true);
            window.history.replaceState({}, document.title, '/#/searching_value=deuda');
            window.location.assign(response.data.processUrl);
        } catch (error) {
            console.error('Error al crear sesión de Placetopay', error);
            const backendMessage = error.response?.data?.message;
            setPaymentError(backendMessage || 'No se pudo iniciar el pago en línea. Verifique los datos e intente nuevamente.');
            setIsRedirectingToPayment(false);
        } finally {
            if (!shouldRedirect) {
                setIsPaying(false);
            }
        }
    };

    const formulariosDisponibles = React.useMemo(() => {
    // Si no hay datos aún, mostrar todos
        if (!data) return formularios;
        
        // Si hay deudas (array con elementos), excluir el certificado de no adeudar
        if (data.length > 0) {
            return formularios.filter(f => f.sigla !== 'CDNA');
        }
        
        // Si no hay deudas (array vacío), mostrar todos incluido el certificado
        return formularios;
    }, [data]);

    const handlePrintFormulario = async () => {
        if (!selectedFormulario) {
            NotificationManager.warning("Por favor seleccione un formulario primero", "", 3000);
            return;
        }

        // NUEVO: Validar que tengamos identificación
        if (!identificacion) {
            NotificationManager.error("No se pudo obtener la identificación del contribuyente", "", 3000);
            return;
        }

        setIsPrinting(true);
        
        try {
            const pdfPath = `${process.env.PUBLIC_URL}/formatos/${selectedFormulario.archivo}`;
            
            console.log('=== INICIO DEBUG ===');
            console.log('Formulario seleccionado:', selectedFormulario);
            console.log('Identificación:', identificacion); // NUEVO: Log de identificación
            console.log('Ruta PDF:', pdfPath);
            
            const response = await fetch(pdfPath);
            
            console.log('Response status:', response.status);
            console.log('Response OK:', response.ok);
            
            if (!response.ok) {
                throw new Error(`No se pudo cargar el archivo PDF. Status: ${response.status}`);
            }
            
            const pdfArrayBuffer = await response.arrayBuffer();
            
            const header = new Uint8Array(pdfArrayBuffer.slice(0, 5));
            const headerString = String.fromCharCode.apply(null, header);
            
            if (!headerString.startsWith('%PDF-')) {
                throw new Error(`El archivo no es un PDF válido. Encabezado: ${headerString}`);
            }
            
            console.log('✓ PDF cargado correctamente');
            console.log('Tamaño:', pdfArrayBuffer.byteLength, 'bytes');
            console.log('Sigla a enviar:', selectedFormulario.sigla);
            console.log('URL del backend:', `${GeneralService.api_url}/cajaunica/rest/caja/pdf/reemplazar?sigla=${selectedFormulario.sigla}&identificacion=${identificacion}`);
            
            // CAMBIO: Enviar al backend con identificación
            const backendResponse = await GeneralService.procesarPdfFormulario(
                pdfArrayBuffer, 
                selectedFormulario.sigla,
                identificacion
            );

            console.log('✓ Backend procesó correctamente');
            console.log('Response data type:', typeof backendResponse.data);
            console.log('Response data size:', backendResponse.data.size);

            const processedPdfBlob = backendResponse.data;
            const pdfUrl = URL.createObjectURL(processedPdfBlob);
            
            const printWindow = window.open(pdfUrl, '_blank');
            
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            } else {
                NotificationManager.warning(
                    "No se pudo abrir la ventana de impresión. Verifique que no esté bloqueando ventanas emergentes.", 
                    "", 
                    5000
                );
            }
            
            NotificationManager.success(
                `Formulario ${selectedFormulario.nombre} preparado para imprimir`, 
                "", 
                3000
            );
            
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
            
        } catch (error) {
            console.error('=== ERROR COMPLETO ===');
            console.error('Error:', error);
            console.error('Error message:', error.message);
            
            // Detalles del error de axios
            if (error.response) {
                console.error('❌ Error del servidor:');
                console.error('Status:', error.response.status);
                console.error('Status Text:', error.response.statusText);
                console.error('Headers:', error.response.headers);
                console.error('Data:', error.response.data);
                
                // Intentar leer el error como texto si es un blob
                if (error.response.data instanceof Blob) {
                    const text = await error.response.data.text();
                    console.error('Error del servidor (texto):', text);
                    NotificationManager.error(
                        `Error del servidor: ${text}`, 
                        "", 
                        7000
                    );
                } else {
                    NotificationManager.error(
                        `Error del servidor (${error.response.status}): ${error.response.data.message || error.response.statusText}`, 
                        "", 
                        7000
                    );
                }
            } else if (error.request) {
                console.error('❌ No hubo respuesta del servidor');
                console.error('Request:', error.request);
                NotificationManager.error(
                    'No se recibió respuesta del servidor', 
                    "", 
                    5000
                );
            } else {
                console.error('❌ Error al configurar la petición');
                NotificationManager.error(
                    `Error: ${error.message}`, 
                    "", 
                    5000
                );
            }
            
            console.error('=== FIN ERROR ===');
        } finally {
            setIsPrinting(false);
        }
    };

    const searchAction = (credentialValue, options = {}) => {
        if (isSearching && !options.force) return;

        // Resetear estados al inicio de cada búsqueda
        setIsSearching(true);
        setShowTable(false);
        setSelectedFormulario(null);
        setData(null);
        setToPay(0);
        setSelectedIds([]);
        setAcceptedTerms(false);
        setAcceptedPrivacyPolicy(false);
        setPaymentError('');
        if (!options.preserveReturnMessage) {
            setPaymentReturnMessage('');
            setPaymentReturnSummary(null);
        }
        setPaymentHistory([]);
        paidTitleIdsRef.current = getPaidTitleIdsFromHistory(options.paidHistoryRows || []);
        setPaymentHistoryError('');
        setPage(0);
        setIdentificacion(credentialValue); // NUEVO: Guardar identificación
        setBuyer({
            ...emptyBuyer,
            documentType: getDocumentTypeByIdentification(credentialValue),
            document: credentialValue,
            surname: isRucIdentification(credentialValue) ? RUC_SURNAME_PLACEHOLDER : '',
        });

        cargarHistorialPlacetopay(credentialValue);

        GeneralService.getDebts(credentialValue).then(result => {
            if (typeof result.data.status !== "undefined") {
                const mensaje = result.data.status.msg || "";

                if (mensaje.toUpperCase().includes("NO TIENE DEUDAS") ||
                    mensaje.toUpperCase().includes("SIN DEUDAS")) {
                    setData([]);
                    setToPay(0);
                    setShowTable(true);
                    NotificationManager.success(mensaje, "", 3000);
                } else {
                    NotificationManager.error(mensaje, "", 5000);
                }
            }
            else {
                NotificationManager.warning("EL CONTRIBUYENTE TIENE DEUDAS", "", 5000);
                const filteredDebts = filterPaidRows(result.data, paidTitleIdsRef.current);
                setData(filteredDebts);
                if (filteredDebts.length > 0) {
                    const first = filteredDebts[0];
                    const isRuc = isRucIdentification(credentialValue);
                    const documentType = getDocumentTypeByIdentification(credentialValue);
                    const businessName = getBusinessName(first);
                    const fullName = `${first.proNombre || ''} ${first.proApellido || ''}`.trim();
                    const parsedName = splitName(fullName);
                    setBuyer({
                        documentType,
                        document: credentialValue,
                        name: isRuc ? businessName : parsedName.name,
                        surname: isRuc ? RUC_SURNAME_PLACEHOLDER : parsedName.surname,
                        email: '',
                        mobile: '',
                    });
                }
                setToPay(calculateDebtTotal(filteredDebts));
                setShowTable(true);
            }
        }, error => {
            NotificationManager.error("Error en consulta por favor intente más tarde.", "", 5000, () => { });
        }).finally(() => {
            setIsSearching(false);
        });
    }

    React.useEffect(() => {
        const refreshPaymentNavigationState = async () => {
            setIsPaying(false);
            setIsRedirectingToPayment(false);

            const credentialValue = (identificacion && identificacion.trim())
                || window.localStorage.getItem(LAST_PAYMENT_IDENTIFICATION_KEY);

            const isDebtHashRoute = window.location.hash.startsWith('#/searching_value=deuda');
            if (isDebtHashRoute) {
                window.history.replaceState({}, document.title, '/consulta_deuda');
            }

            const activeReference = window.sessionStorage.getItem(ACTIVE_PAYMENT_REFERENCE_KEY);
            const shouldRefreshAfterPayment = window.sessionStorage.getItem(PAYMENT_RETURN_PENDING_KEY) === '1';
            if (!activeReference || window.location.search.includes('reference=')) {
                if (credentialValue) {
                    if (isDebtHashRoute || shouldRefreshAfterPayment) {
                        window.sessionStorage.removeItem(PAYMENT_RETURN_PENDING_KEY);
                        searchAction(credentialValue, {
                            preserveReturnMessage: true,
                            force: true,
                        });
                    } else {
                        cargarHistorialPlacetopay(credentialValue);
                    }
                }
                return;
            }

            try {
                const response = await GeneralService.consultarSesionPlacetopay(activeReference);
                const session = response.data || {};
                setPaymentReturnSummary(session);
                const returnIdentification = session.identification || credentialValue;

                if (isApprovedPayment(session.status)) {
                    setPaymentReturnMessage('Transacción aprobada. Se actualizó el estado del pago.');
                    removePaidTitlesFromTable([session]);
                    window.sessionStorage.removeItem(ACTIVE_PAYMENT_REFERENCE_KEY);
                } else if (String(session.status || '').toUpperCase() === 'REJECTED') {
                    setPaymentReturnMessage('Transacción rechazada. Los títulos siguen disponibles para un nuevo intento de pago.');
                    window.sessionStorage.removeItem(ACTIVE_PAYMENT_REFERENCE_KEY);
                } else {
                    setPaymentReturnMessage(`${paymentReturnTitle(session.status)}. La transacción está pendiente en Placetopay.`);
                }

                if (returnIdentification) {
                    window.localStorage.setItem(LAST_PAYMENT_IDENTIFICATION_KEY, returnIdentification);
                    window.sessionStorage.removeItem(PAYMENT_RETURN_PENDING_KEY);
                    searchAction(returnIdentification, {
                        preserveReturnMessage: true,
                        paidHistoryRows: [session],
                        force: true,
                    });
                }
            } catch (error) {
                console.error('No se pudo consultar la referencia activa de Placetopay', error);
                setPaymentReturnMessage('No se pudo confirmar automáticamente el pago. Revise el historial o vuelva a buscar la identificación.');
                if (credentialValue) {
                    window.sessionStorage.removeItem(PAYMENT_RETURN_PENDING_KEY);
                    searchAction(credentialValue, {
                        preserveReturnMessage: true,
                        force: true,
                    });
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshPaymentNavigationState();
            }
        };

        window.addEventListener('pageshow', refreshPaymentNavigationState);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        refreshPaymentNavigationState();

        return () => {
            window.removeEventListener('pageshow', refreshPaymentNavigationState);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identificacion, cargarHistorialPlacetopay, removePaidTitlesFromTable]);

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const reference = params.get('reference');

        if (!reference) return;

        const savedIdentification = window.localStorage.getItem(LAST_PAYMENT_IDENTIFICATION_KEY);

        const refreshPaymentReturn = async () => {
            let latestSession = null;
            try {
                let response = await GeneralService.consultarSesionPlacetopay(reference);
                let session = response.data || {};
                let attempts = 0;

                while (isPendingPayment(session.status) && attempts < PAYMENT_RETURN_RETRY_ATTEMPTS) {
                    await waitPaymentReturnRetry();
                    response = await GeneralService.consultarSesionPlacetopay(reference);
                    session = response.data || {};
                    attempts += 1;
                }

                latestSession = session;
                setPaymentReturnSummary(session);
                window.sessionStorage.removeItem(ACTIVE_PAYMENT_REFERENCE_KEY);

                if (isApprovedPayment(session.status)) {
                    setPaymentReturnMessage('Transacción aprobada. Se actualizó el estado del pago.');
                    NotificationManager.success('Pago aprobado. Actualizando deudas e historial.', '', 5000);
                    removePaidTitlesFromTable([session]);
                } else if (String(session.status || '').toUpperCase() === 'REJECTED') {
                    setPaymentReturnMessage('Transacción rechazada. Los títulos siguen disponibles para un nuevo intento de pago.');
                    NotificationManager.warning('Pago rechazado. Puede intentar nuevamente con los mismos títulos.', '', 5000);
                } else {
                    setPaymentReturnMessage(`${paymentReturnTitle(session.status)}. Se consultó el estado del pago en Placetopay.`);
                    NotificationManager.info(session.message || 'Se consultó el estado del pago.', '', 5000);
                }
            } catch (error) {
                console.error('No se pudo consultar la referencia Placetopay', error);
                setPaymentReturnMessage('No se pudo confirmar automáticamente el pago. Revise el historial o vuelva a buscar la identificación.');
            } finally {
                const returnIdentification = latestSession?.identification || savedIdentification;
                if (returnIdentification) {
                    window.localStorage.setItem(LAST_PAYMENT_IDENTIFICATION_KEY, returnIdentification);
                    window.sessionStorage.removeItem(PAYMENT_RETURN_PENDING_KEY);
                    searchAction(returnIdentification, {
                        preserveReturnMessage: true,
                        paidHistoryRows: latestSession ? [latestSession] : [],
                        force: true,
                    });
                }

                const cleanUrl = `${window.location.pathname}${window.location.hash || ''}`;
                window.history.replaceState({}, document.title, cleanUrl);
            }
        };

        refreshPaymentReturn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={classes.pageShell}>
            <Panel header={"Estado de Cuenta al: " + moment().format("YYYY-MM-DD")} style={{ maxWidth: "100%" }}>
                <SearchArea searchAction={searchAction} loading={isSearching} value={identificacion} />
                {isSearching && (
                    <div className={classes.searchLoader}>
                        <CircularProgress size={22} />
                        <span>Consultando deudas...</span>
                    </div>
                )}
                {paymentReturnSummary && (
                    <div className={paymentReturnClass(paymentReturnSummary.status || paymentReturnSummary.placetopayStatus, classes)}>
                        <div className={classes.paymentReturnHeader}>
                            <span>{paymentReturnTitle(paymentReturnSummary.status || paymentReturnSummary.placetopayStatus)}</span>
                            <span className={classes.paymentReturnBadge}>
                                {paymentStatusLabel(paymentReturnSummary.status || paymentReturnSummary.placetopayStatus)}
                            </span>
                        </div>
                        <div className={classes.paymentReturnGrid}>
                            <div className={classes.paymentReturnItem}>
                                <span>Referencia</span>
                                <strong className={classes.paymentReturnReference}>{paymentReturnSummary.reference || '-'}</strong>
                            </div>
                            <div className={classes.paymentReturnItem}>
                                <span>Valor</span>
                                <strong>{formatPaymentAmount(paymentReturnSummary)}</strong>
                            </div>
                            <div className={classes.paymentReturnItem}>
                                <span>Estado</span>
                                <strong>{paymentStatusLabel(paymentReturnSummary.status || paymentReturnSummary.placetopayStatus)}</strong>
                            </div>
                        </div>
                        <div className={classes.paymentReturnActions}>
                            <Button size="small" color="primary" onClick={clearPaymentReturnSummary}>
                                Cerrar resumen
                            </Button>
                        </div>
                    </div>
                )}
                {paymentReturnMessage && (
                    <div style={{ marginTop: '8px', color: '#455a64', fontWeight: 600, textAlign: 'center' }}>
                        {paymentReturnMessage}
                    </div>
                )}
            </Panel>

            <div className={classes.paymentInstructionsLinkContainer}>
                <button
                    type="button"
                    className={classes.paymentInstructionsLink}
                    onClick={() => setShowPaymentInstructions(true)}
                >
                    Ver instrucciones y datos para realizar pagos
                </button>
            </div>

            {/* Mostrar formularios SIEMPRE que se haya realizado una búsqueda */}
            {showTable && (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Button
                        className={classes.printButton}
                        variant="contained"
                        startIcon={<DescriptionIcon />}
                        onClick={handleMenuOpen}
                    >
                        Seleccionar Formulario
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            style: {
                                maxHeight: 400,
                                width: '450px',
                            },
                        }}
                    >
                        {formulariosDisponibles.map((formulario) => (
                            <MenuItem
                                key={formulario.id}
                                onClick={() => handleFormularioSelect(formulario)}
                                selected={selectedFormulario?.id === formulario.id}
                            >
                                {formulario.nombre}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            )}

            {/* Mostrar formulario seleccionado SIEMPRE que se haya realizado una búsqueda */}
            {showTable && selectedFormulario && (
                <div className={classes.selectedFormContainer}>
                    <div className={classes.formInfo}>
                            <DescriptionIcon style={{ color: '#2f7ea8', fontSize: 32 }} />
                        <div>
                            <h4 style={{ margin: 0 }}>Formulario seleccionado:</h4>
                            <p style={{ margin: '5px 0 0 0', color: '#666' }}>
                                {selectedFormulario.nombre} (Código: {selectedFormulario.sigla})
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PrintIcon />}
                        onClick={handlePrintFormulario}
                        disabled={isPrinting}
                        style={{
                            backgroundColor: '#4CAF50',
                            minWidth: '150px'
                        }}
                    >
                        {isPrinting ? 'Procesando...' : 'Imprimir Formato'}
                    </Button>
                </div>
            )}

            <div className={classes.sectionSpacer}>
            <Panel header={"Valor Total: $" + toPay.toFixed(2).toString()} style={{ maxWidth: "100%" }}>
                {showTable && data && data.length > 0 &&
                    <TableContainer component={Paper} className={classes.tablePaper}>
                        <Table className={classes.table} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="left">Pagar</StyledTableCell>
                                    <StyledTableCell align="left">NOMBRE</StyledTableCell>
                                    <StyledTableCell align="left">Tributo</StyledTableCell>
                                    <StyledTableCell align="left">Clave Catastral</StyledTableCell>
                                    <StyledTableCell align="left">Valor Emitido</StyledTableCell>
                                    <StyledTableCell align="left">Descuento</StyledTableCell>
                                    <StyledTableCell align="left">Recargo</StyledTableCell>
                                    <StyledTableCell align="left">Interes</StyledTableCell>
                                    <StyledTableCell align="left">Total</StyledTableCell>
                                    <StyledTableCell align="left">Estado</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRows.map((row) => (
                                    <StyledTableRow key={getTrcId(row) || row.hegClaveCatastral}>
                                        <StyledTableCell align="left">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(getTrcId(row))}
                                                disabled={getTrcId(row) === null}
                                                onChange={() => toggleSelection(row)}
                                                aria-label="Seleccionar título para pagar"
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="left">{row.proApellido} {row.proNombre}</StyledTableCell>
                                        <StyledTableCell align="left">{row.trbNombre}</StyledTableCell>
                                        <StyledTableCell align="left">{row.hegClaveCatastral}</StyledTableCell>
                                        <StyledTableCell align="left">${row.trc_total_cobro}</StyledTableCell>
                                        <StyledTableCell align="left">${row.trc_valor_descuento}</StyledTableCell>
                                        <StyledTableCell align="left">${row.trc_valor_recargo}</StyledTableCell>
                                        <StyledTableCell align="left">${row.trc_valor_interes}</StyledTableCell>
                                        <StyledTableCell align="left">${row.trcValorEmitido}</StyledTableCell>
                                        <StyledTableCell align="left">{row.estado}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={data.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                }
                {/* Mensaje cuando no hay deudas */}
                {showTable && data && data.length === 0 && (
                    <div className={classes.emptyState}>
                        <h3>✓ El contribuyente no tiene deudas pendientes</h3>
                        <p>Puede seleccionar un formulario para imprimir si lo necesita.</p>
                    </div>
                )}
            </Panel>
            </div>

            {showTable && data && data.length > 0 && (
                <div className={classes.paymentContainer}>
                    <div className={classes.paymentHeader}>
                        <div>
                            <p className={classes.paymentEyebrow}>Pago en línea</p>
                            <h3 className={classes.paymentBrandTitle}>Placetopay</h3>
                            <p className={classes.paymentBrandSubtitle}>
                                Revisa los títulos seleccionados, completa los datos del comprador y continúa al portal seguro de pagos.
                            </p>
                        </div>
                        <div className={classes.paymentLogos}>
                            <img
                                className={classes.placetopayLogo}
                                src="https://static.placetopay.com/placetopay-logo.svg"
                                alt="Placetopay"
                            />
                            <img
                                className={classes.franchiseLogo}
                                src="/portal-assets/franquicias.png"
                                alt="Visa, Mastercard, American Express, Discover y Diners Club"
                            />
                        </div>
                    </div>

                    <div className={classes.paymentBody}>
                        <div className={classes.paymentLayout}>
                            <div className={classes.paymentSummary}>
                                <div className={classes.paymentSummaryHeader}>
                                    <h4>Resumen del pago</h4>
                                    <p>Selecciona uno o varios títulos para calcular el total a pagar.</p>
                                </div>
                                <div className={classes.paymentSummaryRow}>
                                    <span>Títulos seleccionados</span>
                                    <strong>{selectedIds.length}</strong>
                                </div>
                                <div className={classes.paymentSummaryRow}>
                                    <span>Subtotal</span>
                                    <strong>{formatCurrency(selectedSubtotal)}</strong>
                                </div>
                                {selectedIva > 0 && (
                                    <div className={classes.paymentSummaryRow}>
                                        <span>IVA</span>
                                        <strong>{formatCurrency(selectedIva)}</strong>
                                    </div>
                                )}
                                <div className={`${classes.paymentSummaryRow} ${classes.paymentSummaryTotal}`}>
                                    <span>Total a pagar</span>
                                    <strong>{formatCurrency(selectedTotal)}</strong>
                                </div>
                                <div className={classes.paymentSummaryActions}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={toggleAllSelection}
                                        disabled={!payableRows.length}
                                    >
                                        {selectedIds.length === payableRows.length && payableRows.length ? 'Quitar selección' : 'Seleccionar todo'}
                                    </Button>
                                </div>
                            </div>

                            <div className={classes.paymentFormPanel}>
                                <h4 className={classes.paymentFormTitle}>Datos del comprador</h4>
                                <div className={classes.paymentGrid}>
                                    <label>
                                        Tipo documento
                                        <select
                                            className={classes.paymentInput}
                                            value={buyerDocumentType}
                                            onChange={(event) => handleBuyerChange('documentType', event.target.value)}
                                        >
                                            <option value="CI">Cédula</option>
                                            <option value="RUC">RUC</option>
                                            <option value="PAS">Pasaporte</option>
                                        </select>
                                    </label>
                                    <label>
                                        Documento
                                        <input
                                            className={`${classes.paymentInput} ${buyerValidationErrors.document ? classes.paymentInputInvalid : ''}`}
                                            value={buyer.document}
                                            inputMode={buyerDocumentType === 'PAS' ? 'text' : 'numeric'}
                                            aria-invalid={Boolean(buyerValidationErrors.document)}
                                            aria-describedby={buyerValidationErrors.document ? 'buyer-document-error' : undefined}
                                            onChange={(event) => handleBuyerChange('document', event.target.value)}
                                        />
                                        {buyerValidationErrors.document && (
                                            <span id="buyer-document-error" className={classes.fieldErrorText}>{buyerValidationErrors.document}</span>
                                        )}
                                    </label>
                                    <label>
                                        {buyerIsRuc ? 'Razón social' : 'Nombres'}
                                        <input
                                            className={`${classes.paymentInput} ${buyerValidationErrors.name ? classes.paymentInputInvalid : ''}`}
                                            value={buyer.name}
                                            aria-invalid={Boolean(buyerValidationErrors.name)}
                                            aria-describedby={buyerValidationErrors.name ? 'buyer-name-error' : undefined}
                                            onChange={(event) => handleBuyerChange('name', event.target.value)}
                                        />
                                        {buyerValidationErrors.name && (
                                            <span id="buyer-name-error" className={classes.fieldErrorText}>{buyerValidationErrors.name}</span>
                                        )}
                                    </label>
                                    {!buyerIsRuc && (
                                        <label>
                                            Apellidos
                                            <input
                                                className={`${classes.paymentInput} ${buyerValidationErrors.surname ? classes.paymentInputInvalid : ''}`}
                                                value={buyer.surname}
                                                aria-invalid={Boolean(buyerValidationErrors.surname)}
                                                aria-describedby={buyerValidationErrors.surname ? 'buyer-surname-error' : undefined}
                                                onChange={(event) => handleBuyerChange('surname', event.target.value)}
                                            />
                                            {buyerValidationErrors.surname && (
                                                <span id="buyer-surname-error" className={classes.fieldErrorText}>{buyerValidationErrors.surname}</span>
                                            )}
                                        </label>
                                    )}
                                    <label>
                                        Correo electrónico
                                        <input
                                            className={`${classes.paymentInput} ${buyerEmailInvalid ? classes.paymentInputInvalid : ''}`}
                                            type="email"
                                            value={buyer.email}
                                            onChange={(event) => handleBuyerChange('email', event.target.value)}
                                        />
                                        {buyerEmailInvalid && (
                                            <span className={classes.fieldErrorText}>Ingrese un correo válido, por ejemplo usuario@dominio.com.</span>
                                        )}
                                    </label>
                                    <label>
                                        Celular
                                        <input
                                            className={`${classes.paymentInput} ${buyerMobileInvalid ? classes.paymentInputInvalid : ''}`}
                                            value={buyer.mobile}
                                            inputMode="numeric"
                                            maxLength="10"
                                            onChange={(event) => handleBuyerChange('mobile', event.target.value)}
                                        />
                                        {buyerMobileInvalid && (
                                            <span className={classes.fieldErrorText}>Ingrese un celular válido de 10 dígitos que empiece con 09.</span>
                                        )}
                                    </label>
                                </div>

                                <div className={classes.termsBox}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={acceptedTerms}
                                            onChange={(event) => setAcceptedTerms(event.target.checked)}
                                        />
                                        <span>
                                            Acepto los{' '}
                                            <button
                                                type="button"
                                                className={classes.termsLink}
                                                onClick={() => setShowTerms(true)}
                                            >
                                                términos y condiciones
                                            </button>{' '}
                                            para utilizar el canal Botón de Pagos.
                                        </span>
                                    </label>
                                </div>

                                <div className={classes.termsBox}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={acceptedPrivacyPolicy}
                                            onChange={(event) => setAcceptedPrivacyPolicy(event.target.checked)}
                                        />
                                        <span>
                                            Acepto la{' '}
                                            <button
                                                type="button"
                                                className={classes.termsLink}
                                                onClick={() => setShowPrivacyPolicy(true)}
                                            >
                                                política de privacidad y autorización para el tratamiento de datos personales
                                            </button>
                                            .
                                        </span>
                                    </label>
                                </div>

                                {paymentError && (
                                    <div className={classes.paymentError}>
                                        {paymentError}
                                    </div>
                                )}

                                <div className={classes.paymentActions}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CreditCardIcon />}
                                        onClick={handlePay}
                                        disabled={!canPay || isPaying || isRedirectingToPayment}
                                    >
                                        {isRedirectingToPayment ? 'Redirigiendo a Placetopay...' : isPaying ? 'Creando sesión...' : 'Pagar con Placetopay'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Dialog
                open={showTerms}
                onClose={() => setShowTerms(false)}
                fullWidth
                maxWidth="md"
                aria-labelledby="terms-dialog-title"
                PaperProps={{ className: classes.dialogPaper }}
            >
                <DialogTitle id="terms-dialog-title" className={classes.dialogTitle}>Términos y condiciones del Botón de Pagos</DialogTitle>
                <DialogContent dividers className={classes.termsContent}>
                    {termsSections.map(section => (
                        <div key={section.title}>
                            <h4>{section.title}</h4>
                            {(Array.isArray(section.body) ? section.body : [section.body]).map((paragraph, index) => (
                                <p key={`${section.title}-${index}`}>
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    ))}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={() => setShowTerms(false)} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showPrivacyPolicy}
                onClose={() => setShowPrivacyPolicy(false)}
                fullWidth
                maxWidth="md"
                aria-labelledby="privacy-policy-dialog-title"
                PaperProps={{ className: classes.dialogPaper }}
            >
                <DialogTitle id="privacy-policy-dialog-title" className={classes.dialogTitle}>{privacyPolicy.title}</DialogTitle>
                <DialogContent dividers className={classes.termsContent}>
                    {renderPrivacyPolicyContent(privacyPolicy.content)}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={() => setShowPrivacyPolicy(false)} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showPaymentInstructions}
                onClose={() => setShowPaymentInstructions(false)}
                fullWidth
                maxWidth="md"
                aria-labelledby="payment-instructions-dialog-title"
                PaperProps={{ className: classes.dialogPaper }}
            >
                <DialogTitle id="payment-instructions-dialog-title" className={classes.dialogTitle}>Instrucciones para realizar pagos</DialogTitle>
                <DialogContent dividers className={classes.paymentInstructionsContent}>
                    <div className={classes.paymentInstructionsGrid}>
                        <div>
                            <h3><b>REALICE SUS PAGOS MEDIANTE LA BANCA WEB (DISPONIBLE TODOS LOS DIAS DE 10HAM A 10HPM)</b></h3>
                            <ul>
                                <li><h5>BANCA VIRTUAL BANCO PICHINCHA, Opción PAGAR SERVICIOS, Búscanos como GAD PEDRO VICENTE M, De inmediato se mostrará el valor a pagar HACER CLIC en PAGAR (Tu pago se verá reflejado al siguiente día y recibirás tu factura al correo registrado).</h5></li>
                                <li>Para pago de agua colocar Cuenta de Usuario y para Impuestos colocar número de cedula</li>
                            </ul>

                            <h3><b>REALICE SUS PAGOS MEDIANTE RED (DISPONIBLE LAS 24 HRS)</b></h3>
                            <ul>
                                <li><h5>RED FACILITO</h5></li>
                            </ul>

                            <h3><b>REALICE SUS PAGOS DE MANERA PRESENCIAL (De lunes a viernes de 08h a 16hpm)</b></h3>
                            <ul>
                                <li><h5>Ventanilla de recaudación de las Oficinas del GADMPVM</h5></li>
                            </ul>

                            <h3><b>UTILIZA NUESTRO WHATSAPP 0986929523 PARA REPORTAR TUS PAGOS REALIZADOS MEDIANTE TRANSFERENCIA BANCARIA.</b></h3>
                            <ul>
                                <li><h5>Sólo mensajes, coloca datos del contribuyente y el detalle del tributo pagado.</h5></li>
                            </ul>
                        </div>

                        <div>
                            <ul>
                                <li><h5><b>DEPOSITOS O TRANSFERENCIA:</b> BANCO PICHINCHA</h5></li>
                                <li><h5><b>CUENTA CORRIENTE:</b> 3247002304</h5></li>
                                <li><h5><b>BENFICIARIO:</b> GADMPVM</h5></li>
                                <li><h5><b>RUC:</b> 1760009530001</h5></li>
                                <li><h5><b>SUBLINEA:</b> 30200 EN CASO DE DEPOSITO</h5></li>
                                <li><h5><b>CORREO:</b> tesoreria@pedrovicentemaldonado.gob.ec</h5></li>
                                <li><h5><b>TELEFONOS:</b> 2392282 / 2392283 EXT 217</h5></li>
                                <li><h5><b>IMPORTANTE.-</b> Enviar número de cédula o RUC del propietario del predio para registrar su pago.</h5></li>
                                <li><h5><b>NOTA.- </b>Si sus datos no constan en la consulta, deberá acercarse con su cedula de identidad al área de Avalúos y Catastros, para actualizar sus datos.</h5></li>
                            </ul>
                        </div>
                    </div>
                    <div className={classes.paymentInstructionsReminder}>
                        Recuerda que: Los pagos realizados mediante transferencia bancaria, deben ser reportados oportunamente al canal de whatsapp, caso contrario, se te sumará el pago de intereses.
                    </div>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={() => setShowPaymentInstructions(false)} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {showTable && (
                <div className={classes.historyContainer}>
                    <Panel header="Historial de transacciones Placetopay" style={{ maxWidth: "100%" }}>
                        {paymentHistoryError && (
                            <div style={{ padding: '12px', color: '#b71c1c' }}>
                                {paymentHistoryError}
                            </div>
                        )}
                        <TableContainer component={Paper} className={classes.tablePaper}>
                            <Table aria-label="historial placetopay">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell align="left">Fecha y hora</StyledTableCell>
                                        <StyledTableCell align="left">Referencia</StyledTableCell>
                                        <StyledTableCell align="left">Solicitud</StyledTableCell>
                                        <StyledTableCell align="left">Estado</StyledTableCell>
                                        <StyledTableCell align="left">Total</StyledTableCell>
                                        <StyledTableCell align="left">Autorización</StyledTableCell>
                                        <StyledTableCell align="left">Recibo</StyledTableCell>
                                        <StyledTableCell align="left">Cobro Caja</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paymentHistory.length > 0 ? paginatedPaymentHistory.map(payment => (
                                        <StyledTableRow key={payment.transactionId || payment.reference}>
                                            <StyledTableCell align="left">{formatPaymentDate(payment.createdAt)}</StyledTableCell>
                                            <StyledTableCell align="left">{payment.reference || '-'}</StyledTableCell>
                                            <StyledTableCell align="left">{payment.requestId || '-'}</StyledTableCell>
                                            <StyledTableCell align="left">{paymentStatusLabel(payment.status)}</StyledTableCell>
                                            <StyledTableCell align="left">{payment.currency || 'USD'} {Number(payment.total || 0).toFixed(2)}</StyledTableCell>
                                            <StyledTableCell align="left">{payment.authorization || '-'}</StyledTableCell>
                                            <StyledTableCell align="left">{payment.receipt || '-'}</StyledTableCell>
                                            <StyledTableCell align="left">{isCashierChargeGenerated(payment) ? 'Generado' : 'Pendiente'}</StyledTableCell>
                                        </StyledTableRow>
                                    )) : (
                                        <StyledTableRow>
                                            <StyledTableCell align="center" colSpan={8}>
                                                No registra pagos en línea para esta identificación.
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={paymentHistory.length}
                                page={historyPage}
                                rowsPerPage={historyRowsPerPage}
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                labelRowsPerPage="Filas por página:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                onChangePage={handleChangeHistoryPage}
                                onChangeRowsPerPage={handleChangeHistoryRowsPerPage}
                            />
                        </TableContainer>
                    </Panel>
                </div>
            )}
        </div>
    );
}
